import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { Typenames } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import TransferConfirmBottomSheetView from 'features/v2a/sender/components/TransferConfirmBottomSheetView/TransferConfirmBottomSheetView';
import TransferLimitMessage from 'features/v2a/sender/components/TransferLimitMessage';
import {
  displayInfo,
  EXTERNAL_USER,
  getAvatarInitials,
  getAvatarTheme,
  getIsPhone,
  getPrimaryContact,
  getRecipientContactName,
  shouldUseInitialsForAvatar,
} from 'features/v2a/sender/helpers/Sender.helpers';
import useSearchRecipient from 'features/v2a/sender/hooks/useSearchRecipient';
import useV2AOptions from 'features/v2a/sender/hooks/useV2AOptions';
import useV2ASubmitTransfer from 'features/v2a/sender/hooks/useV2ASubmitTransfer';
import useV2ATransferDetails from 'features/v2a/sender/hooks/useV2ATransferDetails';
import { V2AContext } from 'features/v2a/sender/provider/V2AContextProvider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Keyboard,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import TransferField from 'shared/components/vxl/fields/TransferField/TransferField';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import NoteInput from 'shared/components/vxl/input/NoteInput/NoteInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { applyKeyToAmount } from 'shared/utils/numberUtils';
import { allowOnlyOneSpace } from 'shared/utils/string/string.helpers';

const customRenderers = {
  li: ({ TDefaultRenderer, ...props }) => {
    return (
      <View style={styles.transferLimitBullet}>
        <BodyBaseText>
          <TDefaultRenderer {...props} />
        </BodyBaseText>
      </View>
    );
  },
};

const TransferScreen = () => {
  const router = useRouter();
  const { selectedRecipient, setConfirmationData, setSelectedRecipient } =
    useContext(V2AContext);
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'sender',
  });
  const { defaultMessage, defaultTitle } = useDefaultErrorStrings();
  const transferLimitBottomSheetRef = useRef<BottomSheetModal>(null);
  const [isAmountInputSelected, setIsAmountInputSelected] =
    useState<boolean>(false);
  const [transferNote, setTransferNote] = useState<string | undefined>(
    undefined,
  );
  const [agreementsChecked, setAgreementsChecked] = useState(false);
  useViewDidFocusScreenAnalyticsEvent({ key: 'move-money/v2a/transfer' });
  const { isFirstTime } = useV2AOptions();

  const [transferAmount, setTransferAmount] = useState<string | undefined>(
    '0.00',
  );
  const [shouldOpenBottomSheet, setShouldOpenBottomSheet] = useState(false);

  const logV2aTransferAmountEnteredEvent = usePressableAnalytics({
    analyticsId: 'V2aTransferAmountEntered',
    customAttributes: { amount: transferAmount ?? '' },
  });

  const {
    recipients: searchedRecipient,
    fetching,
    searchUser,
  } = useSearchRecipient();

  const { submitTransfer, fetching: submitTransferFetching } =
    useV2ASubmitTransfer();
  const {
    transferAgreement,
    isSubmitButtonDisable,
    maxLimit,
    confirmationMessage,
    fetchTransferLimit,
    transferLimitTitle,
    transferLimitMessage,
    transferLimitFetching,
  } = useV2ATransferDetails(transferAmount, searchedRecipient);

  const openBottomSheet = () => {
    logV2aTransferAmountEnteredEvent();
    bottomSheetRef.current?.present();
  };

  const [contactName, setContactName] = useState<string>(
    getRecipientContactName(selectedRecipient),
  );

  useEffect(() => {
    const primaryContact = getPrimaryContact(selectedRecipient);
    if (!searchedRecipient && primaryContact) {
      searchUser(primaryContact);
    }
    //Should run once when the component mounts, and prevent infinite call
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeBottomSheet = () => {
    bottomSheetRef.current?.close();
  };

  const openTransferLimitBottomSheet = () => {
    transferLimitBottomSheetRef.current?.present();
  };

  const closeTransferLimitBottomSheet = () => {
    transferLimitBottomSheetRef.current?.close();
  };

  const handleConfirmation = async () => {
    closeBottomSheet();
    if (
      !selectedRecipient ||
      !transferAmount ||
      (transferAgreement && !agreementsChecked)
    ) {
      return;
    }

    const confirmedDisplayName =
      allowOnlyOneSpace(contactName) ||
      (searchedRecipient
        ? allowOnlyOneSpace(searchedRecipient.displayName)
        : '');
    const submissionOutcome = await submitTransfer(
      {
        recipient: selectedRecipient,
        amount: transferAmount,
        note: transferNote ?? '',
      },
      confirmedDisplayName,
      transferAgreement?.id,
    );

    if (submissionOutcome.status === 'cancelled') {
      return;
    }

    if (submissionOutcome.status === 'failure') {
      setConfirmationData({
        errorTitle: submissionOutcome.title ?? defaultTitle,
        errorMessage: submissionOutcome.message ?? defaultMessage,
        errorCode: submissionOutcome.code ?? '',
      });
    } else {
      setConfirmationData({
        educationMessage: submissionOutcome.data.educationMessage,
        headerTitle: submissionOutcome.data.successTitle,
        successMessage: submissionOutcome.data.sentMessage,
        ctaTitle: submissionOutcome.data.dismissButton?.button?.copy?.copy,
        amount: submissionOutcome.amount,
        isFirstTime,
      });
    }

    router.replace('/move-money/v2a/transfer-success');
  };

  const handleNumPadAction = (value: string) => {
    const newAmount = applyKeyToAmount(transferAmount ?? '', value);
    if (newAmount !== undefined && newAmount !== transferAmount) {
      setTransferAmount(newAmount);
    }
  };

  const getContactName: string = shouldUseInitialsForAvatar(contactName)
    ? contactName
    : '';

  const initialChars = getAvatarInitials(
    searchedRecipient?.__typename === Typenames.V2AInternalRecipient
      ? searchedRecipient
      : getContactName,
  );

  const avatarVariant = selectedRecipient &&
    (selectedRecipient.id !== EXTERNAL_USER ||
      !getIsPhone(selectedRecipient) ||
      contactName) && {
      ...{
        userAvatarVariant: {
          initials: initialChars,
        },
      },
    };

  const handleInfoIconPress = () => {
    fetchTransferLimit();
    setShouldOpenBottomSheet(true);
  };

  const recipientName = (): string => {
    let value: string | undefined =
      searchedRecipient?.__typename === Typenames.V2AInternalRecipient
        ? searchedRecipient.displayName
        : contactName;
    value =
      value && value.length > 0
        ? value
        : selectedRecipient && displayInfo(selectedRecipient);

    return value ? allowOnlyOneSpace(value) : '';
  };

  useEffect(() => {
    if (searchedRecipient) {
      setSelectedRecipient(searchedRecipient);
    }
  }, [searchedRecipient, setSelectedRecipient]);

  useEffect(() => {
    if (shouldOpenBottomSheet && transferLimitTitle) {
      openTransferLimitBottomSheet();
      setShouldOpenBottomSheet(false);
    }
  }, [transferLimitTitle, shouldOpenBottomSheet]);

  return (
    <>
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={null}
      />
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <TransferField
          primaryText={
            searchedRecipient?.__typename === Typenames.V2AInternalRecipient
              ? searchedRecipient.displayName
              : contactName
          }
          placeholderText={translatedText('v2aTransfer.addNickname')}
          secondaryText={selectedRecipient && displayInfo(selectedRecipient)}
          labelText={translatedText('to')}
          isEditable={
            searchedRecipient?.__typename !== Typenames.V2AInternalRecipient
          }
          showChevron={false}
          avatarAccessory={{
            name: VXLComponentId.USER_AVATAR,
            id: 'user-initials',
            props: {
              iconAccessibilityLabel: 'initials-logo',
              variant: 'MD',
              userAvatarTheme: getAvatarTheme(initialChars),
              userAvatarShowVaroLogo:
                searchedRecipient?.__typename ===
                Typenames.V2AInternalRecipient,
              ...avatarVariant,
            },
          }}
          onChangeText={(item) => {
            setContactName(item);
          }}
          onPress={() => {
            setIsAmountInputSelected(false);
          }}
        />
        <View style={styles.forNote}>
          <NoteInput
            testID="note-input-forNote"
            primaryText={translatedText('forNoteTitle')}
            placeholderText={translatedText('forNotePlaceholder')}
            maxLength={20}
            onChangeText={(item) => {
              setTransferNote(item);
            }}
            gap={26}
            onFocus={() => {
              setIsAmountInputSelected(false);
            }}
          />
        </View>
        <Pressable
          style={styles.amountTextStyle}
          onPress={() => {
            Keyboard.dismiss();
            setIsAmountInputSelected(true);
          }}
          testID="amount"
        >
          {transferAmount ? (
            <AmplitudeMask>
              <ExpressiveXXLText>{`$` + transferAmount}</ExpressiveXXLText>
            </AmplitudeMask>
          ) : null}
        </Pressable>
        <View style={styles.footerContainer}>
          <View style={styles.transferLimitContainer}>
            {maxLimit && transferAmount ? (
              <>
                <TransferLimitMessage
                  transferAmount={transferAmount}
                  maxLimit={maxLimit}
                />
                <IconButton
                  iconKey="INFO"
                  iconSize="MD"
                  iconOnPress={handleInfoIconPress}
                  iconButtonAccessibilityLabel="INFO"
                />
              </>
            ) : (
              <LoadingSpinner />
            )}
          </View>
        </View>
      </ScrollView>
      <View style={styles.numberPad}>
        <NumberPad
          submitButtonLabel={translatedText('button')}
          handleButtonPress={handleNumPadAction}
          handleSubmit={openBottomSheet}
          submitButtonLoading={submitTransferFetching}
          disabled={false}
          submitDisabled={!!isSubmitButtonDisable}
          isKeyboardFocused={isAmountInputSelected}
        />
      </View>
      <BottomSheet ref={bottomSheetRef}>
        <TransferConfirmBottomSheetView
          handleConfirmation={handleConfirmation}
          agreement={transferAgreement}
          confirmationMessage={confirmationMessage}
          transferAmount={transferAmount}
          closeBottomSheet={closeBottomSheet}
          transferNote={transferNote}
          fetching={submitTransferFetching}
          selectedRecipient={searchedRecipient}
          contactName={recipientName()}
          handleCheckAgreement={(isChecked: boolean) =>
            setAgreementsChecked(isChecked)
          }
          agreementsChecked={agreementsChecked}
        />
      </BottomSheet>

      <BottomSheet ref={transferLimitBottomSheetRef}>
        <View style={styles.transferLimit}>
          <TitleMdText>{transferLimitTitle}</TitleMdText>
          <HTMLRenderer
            htmlAsString={transferLimitMessage}
            contentWidth={100}
            tagsStyles={transferLimitStyles}
            renderers={customRenderers}
          />
          <Button
            label={translatedText('okay')}
            type="PRIMARY"
            onPress={closeTransferLimitBottomSheet}
            isLoading={transferLimitFetching}
          />
        </View>
      </BottomSheet>
    </>
  );
};

const styles = StyleSheet.create({
  amountTextStyle: {
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: theme.spacing.m,
    marginVertical: theme.spacing.xl,
  },
  footerContainer: {
    alignItems: 'center',
  },
  forNote: {
    paddingHorizontal: theme.spacing.m,
  },
  numberPad: {
    paddingHorizontal: theme.spacing.m,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  transferLimit: {
    padding: theme.spacing.m,
  },
  transferLimitBullet: { paddingLeft: theme.spacing.s },
  transferLimitContainer: {
    alignItems: 'center',
    color: theme.color.text.primary,
    flexDirection: 'row',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.s,
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
});

const transferLimitStyles = StyleSheet.create({
  // Disabled ESLint for unused styles in this file, as they're passed to the HTMLRenderer component.
  // eslint-disable-next-line react-native/no-unused-styles
  li: {
    paddingBottom: theme.spacing.s,
  },
  // eslint-disable-next-line react-native/no-unused-styles
  ul: {
    fontSize: 16,
    paddingLeft: 18,
  },
});

export default TransferScreen;
